import { Upload } from 'tus-js-client';

export interface ResumableUploadTask {
  start(): Promise<void>;
  abort(): Promise<void>;
}

/** Shared byte transport. Callers retain their own authorization and finalization. */
export function createResumableUploadTask(input: {
  file: File;
  endpoint: string;
  headers: Record<string, string>;
  bucket: string;
  path: string;
  contentType: string;
  fingerprintNamespace: string;
  onProgress: (percentage: number) => void;
  runningError?: () => Error;
  cancelledError?: () => Error;
}): ResumableUploadTask {
  let pending: { resolve(): void; reject(error: Error): void } | null = null;
  let generation = 0;
  let aborting = false;
  const upload = new Upload(input.file, {
    endpoint: input.endpoint,
    headers: input.headers,
    metadata: {
      bucketName: input.bucket, objectName: input.path,
      contentType: input.contentType, cacheControl: '3600'
    },
    fingerprint: async () => [
      input.fingerprintNamespace, input.path, input.file.name,
      input.file.size, input.file.lastModified
    ].join('::'),
    uploadDataDuringCreation: true,
    chunkSize: 6 * 1024 * 1024,
    retryDelays: [0, 3000, 5000, 10000, 20000],
    removeFingerprintOnSuccess: true,
    onProgress: (uploaded, total) => {
      if (pending) {
        input.onProgress(total > 0 ? Math.round(uploaded / total * 100) : 0);
      }
    },
    onError: (error) => {
      const current = pending;
      pending = null;
      current?.reject(error);
    },
    onSuccess: () => {
      const current = pending;
      pending = null;
      if (current) {
        input.onProgress(100);
        current.resolve();
      }
    }
  });
  return {
    async start() {
      if (pending || aborting) {
        throw input.runningError?.() ?? new Error('This upload is already running.');
      }
      const run = ++generation;
      await new Promise<void>((resolve, reject) => {
        pending = { resolve, reject };
        upload.findPreviousUploads().then(previous => {
          // An abort while reading the fingerprint store must not restart the upload.
          if (run !== generation || !pending) {
            return;
          }
          if (previous.length) {
            upload.resumeFromPreviousUpload(previous[0]);
          }
          upload.start();
        }).catch((error: Error) => {
          if (run !== generation) {
            return;
          }
          pending = null;
          reject(error);
        });
      });
    },
    async abort() {
      generation++;
      aborting = true;
      const current = pending;
      pending = null;
      try {
        await upload.abort(false);
      } finally {
        aborting = false;
        current?.reject(input.cancelledError?.() ?? new Error('Upload cancelled.'));
      }
    }
  };
}
